import { LoadingState } from '@/components/primitives/LoadingState'
import { PageHeader } from '@/components/primitives/PageHeader'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  fetchStaffAgentRuns,
  fetchStaffAgents,
  killStaffAgentRun
} from '@/lib/p5'
import { notifyError, queryClient } from '@/lib/query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, Skull } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export function StaffAgentsPage() {
  const [killingId, setKillingId] = useState<number | null>(null)

  const agents = useQuery({
    queryKey: ['staff-agents'],
    queryFn: fetchStaffAgents,
    refetchInterval: 60_000
  })

  const runs = useQuery({
    queryKey: ['staff-agent-runs'],
    queryFn: () => fetchStaffAgentRuns(),
    refetchInterval: 30_000
  })

  const kill = useMutation({
    mutationFn: (id: number) => killStaffAgentRun(id),
    onSuccess: () => {
      toast.success('Run killed')
      queryClient.invalidateQueries({ queryKey: ['staff-agent-runs'] })
      setKillingId(null)
    },
    onError: (err) => notifyError('Kill failed', err)
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Staff Agents"
        description="xobstaff workflow agents — config + recent runs. Kill a run if it's stuck or burning cost."
      />

      <div>
        <h3 className="mb-2 text-sm font-semibold">Agents</h3>
        {agents.isLoading ? (
          <LoadingState variant="tiles" rows={6} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {(agents.data?.agents ?? []).map((a) => (
              <Card key={a.agent_name ?? '?'} className="rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {a.agent_name}
                  </span>
                  {a.is_killed && <Badge variant="destructive">killed</Badge>}
                </div>
                <div className="mt-1 text-sm font-medium">{a.model ?? '—'}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Today's runs</div>
                    <div className="text-base font-semibold tabular-nums">
                      {a.today_runs}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Today's cost</div>
                    <div className="text-base font-semibold tabular-nums">
                      ${a.today_cost_usd.toFixed(2)}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            {(agents.data?.agents ?? []).length === 0 && (
              <div className="col-span-full text-sm text-muted-foreground">
                No agents configured.
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Recent runs</h3>
        <Card className="rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">When</th>
                <th className="px-4 py-2.5 text-left">Agent</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-right">Tokens</th>
                <th className="px-4 py-2.5 text-right">Cost</th>
                <th className="px-4 py-2.5 text-right" />
              </tr>
            </thead>
            <tbody>
              {(runs.data?.results ?? []).map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {r.created_at
                      ? new Date(r.created_at).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {r.agent ?? '?'}
                  </td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={
                        r.status === 'running'
                          ? 'secondary'
                          : r.status === 'error'
                            ? 'destructive'
                            : 'outline'
                      }
                    >
                      {r.status ?? '?'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {r.tokens_in + r.tokens_out}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    ${r.cost_usd.toFixed(4)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {r.status === 'running' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setKillingId(r.id)}
                      >
                        <Skull className="size-3.5" /> Kill
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {(runs.data?.results ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-sm text-muted-foreground"
                  >
                    No recent runs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <AlertDialog
        open={killingId !== null}
        onOpenChange={(v) => !v && !kill.isPending && setKillingId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kill run #{killingId}?</AlertDialogTitle>
            <AlertDialogDescription>
              Marks the run as killed. Any in-flight LLM call cannot be
              cancelled retroactively, but no new tokens will be billed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={kill.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={kill.isPending}
              onClick={() => killingId && kill.mutate(killingId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {kill.isPending && <Loader2 className="size-4 animate-spin" />}
              Kill run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
